import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import apiManager from '../../../../api/api-manager';
import {
  PersonaTipoUtenteEnum,
  RecuperoCredenzialiRequest,
  RecuperoPasswordError,
  RicercaUtentiResponse,
} from '../../../../api/generated';
import { parseServerDateTime } from '../../../../api/parsers/commons-api-parsers';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import { AdminConsoleState, AdminConsoleUser } from '../admin-console-types';
import {
  AdminConsoleDocument,
  CustomerServiceGenericError,
  CustomerServiceSearchRequestParams,
  CustomerServiceState,
  SearchError,
  SearchResponse,
} from './customer-service-types';
import {
  mapAdminConsoleDocumentKeyToOrdinamentoUtenti,
  verifySearchFilterFields,
} from './customer-service-utils';
import { parseApiResponseError } from '../../../../api/api-error';

const initialState: CustomerServiceState = {
  searchRequestStatus: 'idle',
  documents: [],
  credentialRecoveryStatus: 'idle',
  unlockUserStatus: 'idle',
  paginationRequestStatus: 'idle',
  pagination: { count: 0, page: 0, perPage: 0, sorting: {} },
  sorting: {},
};

export const searchCustomerService = createAsyncThunk<
  SearchResponse,
  CustomerServiceSearchRequestParams,
  AsyncThunkConfig<SearchError>
>(
  'search-customer-service',
  async (params: CustomerServiceSearchRequestParams, thunkApi) => {
    try {
      const validationError = verifySearchFilterFields(params.filter);
      if (validationError) {
        return thunkApi.rejectWithValue(validationError);
      }

      const user: AdminConsoleUser =
        thunkApi.getState().features.adminConsole.common.user;
      const token = user.token;

      const userCode =
        mapAdminConsoleDocumentKeyToOrdinamentoUtenti[
          Object.keys(params.pagination.sorting)?.[0]
        ] || 'CODICE_UTENTE';

      const sorting = Object.values(params.pagination.sorting)?.[0] === 'DESC';

      const result = await apiManager.adminConsoleApi.cercaUtenti(
        userCode,
        sorting,
        params.pagination?.page,
        params.filter?.ownerName,
        params.filter?.ownerSurname,
        params.filter?.ragioneSociale,
        params.filter?.customerCode,
        params.filter?.status,
        {
          headers: { Authorization: `BEARER ${token}` },
        }
      );

      const listaUtenti: RicercaUtentiResponse = result.data;
      const users: AdminConsoleDocument[] = listaUtenti.data.map((utente) => ({
        userType: utente.utente.ragioneSociale
          ? PersonaTipoUtenteEnum.Pg
          : PersonaTipoUtenteEnum.Pf,
        codiceCliente: utente.utente.codiceUtente,
        intestatario:
          utente.utente.ragioneSociale ||
          `${utente.utente.nome} ${utente.utente.cognome}`,
        figuraAccessoria: !utente.utente.ragioneSociale
          ? ''
          : `${utente.utente.nome} ${utente.utente.cognome}`,
        dataAttivazioneServizio: parseServerDateTime(
          utente.dataAttivazioneServizio
        ),
        stato: utente.statoUtenza,
        email: utente.utente.email,
        telefono: utente.utente.cellulare,
      }));
      return {
        adminConsoleDocuments: users,
        adminConsolePagination: {
          count:
            listaUtenti.pagination.elementiPerPagina *
            listaUtenti.pagination.pagineTotali,
          perPage: listaUtenti.pagination.elementiPerPagina,
          page: listaUtenti.pagination.paginaCorrente,
          sorting: params.pagination.sorting || { codiceCliente: 'ASC' },
        },
      };
    } catch (error) {
      console.error(error);
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
);

export const customerServiceCredentialRecovery = createAsyncThunk<
  any,
  RecuperoCredenzialiRequest,
  AsyncThunkConfig<CustomerServiceGenericError>
>(
  'customerServiceCredentialRecovery',
  async (params: RecuperoCredenzialiRequest, thunkApi) => {
    try {
      const state: AdminConsoleState =
        thunkApi.getState().features.adminConsole.common;
      const response = await apiManager.adminConsoleApi.recuperaCredenziali(
        params,
        {
          headers: { Authorization: `BEARER ${state.user?.token}` },
        }
      );
      if (response.status === 200) {
        return { status: response.status, name: '' };
      } else {
        return {};
      }
    } catch (error) {
      const apiError = parseApiResponseError<RecuperoPasswordError>(error);
      const errorCodes = apiError.payload?.codiciErrore;
      if (errorCodes && errorCodes.indexOf('UTENZA_NON_MIGRATA') >= 0) {
        return thunkApi.rejectWithValue('NOT_MIGRATED_USER');
      }
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
);

export const customerServiceUnlockUser = createAsyncThunk<
  string,
  string,
  AsyncThunkConfig<CustomerServiceGenericError>
>('customerServiceUnlockUser', async (userCode: string, thunkApi) => {
  try {
    const state: AdminConsoleState =
      thunkApi.getState().features.adminConsole.common;
    await apiManager.adminConsoleApi.inizializzazioneSbloccoUtenzaDaConsole(
      userCode,
      {
        headers: { Authorization: `BEARER ${state.user.token}` },
      }
    );
    return null;
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

const customerServiceSlice = createSlice({
  name: 'features/adminConsole/customerService',
  initialState,
  reducers: {
    resetCustomerServiceSearchState(state) {
      state.searchError = undefined;
      state.searchRequestStatus = 'idle';
    },
    resetCustomerServiceCredentialRecoveryState(state) {
      state.credentialRecoveryError = undefined;
      state.credentialRecoveryStatus = 'idle';
    },
    resetCustomerServiceUnlockUserState(state) {
      state.unlockUserError = undefined;
      state.unlockUserStatus = 'idle';
    },
    resetCustomerServiceDocuments(state) {
      state.documents = [];
      state.pagination = { count: 0, page: 0, perPage: 0, sorting: {} };
      state.sorting = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchCustomerService.pending, (state) => {
      state.searchRequestStatus = 'loading';
    });
    builder.addCase(
      searchCustomerService.fulfilled,
      (state, action: PayloadAction<SearchResponse>) => {
        state.searchRequestStatus = 'succeeded';
        state.pagination = action.payload.adminConsolePagination;
        state.documents = action.payload.adminConsoleDocuments;
      }
    );
    builder.addCase(searchCustomerService.rejected, (state, action) => {
      state.searchError = action.payload || 'GENERIC_ERROR';
      state.searchRequestStatus = 'failed';
    });
    builder.addCase(customerServiceCredentialRecovery.pending, (state: any) => {
      state.credentialRecoveryStatus = 'loading';
    });
    builder.addCase(
      customerServiceCredentialRecovery.fulfilled,
      (state: any) => {
        state.credentialRecoveryStatus = 'succeeded';
      }
    );
    builder.addCase(
      customerServiceCredentialRecovery.rejected,
      (state, action) => {
        state.credentialRecoveryError = action.payload || 'GENERIC_ERROR';
        state.credentialRecoveryStatus = 'failed';
      }
    );
    builder.addCase(customerServiceUnlockUser.pending, (state) => {
      state.unlockUserStatus = 'loading';
    });
    builder.addCase(customerServiceUnlockUser.fulfilled, (state) => {
      state.unlockUserStatus = 'succeeded';
    });
    builder.addCase(customerServiceUnlockUser.rejected, (state, action) => {
      state.unlockUserError = action.payload || 'GENERIC_ERROR';
      state.unlockUserStatus = 'failed';
    });
  },
});

export const {
  resetCustomerServiceDocuments,
  resetCustomerServiceSearchState,
  resetCustomerServiceCredentialRecoveryState,
  resetCustomerServiceUnlockUserState,
} = customerServiceSlice.actions;

export default customerServiceSlice.reducer;
